import type { PcmBuffer } from './types';

// Peak-scale a buffer so its loudest sample hits ±target (default 1).
// Operates on the max absolute amplitude across all channels so stereo
// imaging is preserved — we never scale channels independently. Silent
// input is returned unchanged (no division-by-zero, no accidental noise
// amplification).

export type NormalizeOptions = {
  /** Peak target amplitude. Default 1.0 (full scale). Values <1 leave
   *  headroom; values >1 are allowed but get clipped at encode time. */
  target?: number;
};

export function normalize(
  pcm: PcmBuffer,
  opts: NormalizeOptions = {},
): PcmBuffer {
  const target = opts.target ?? 1;
  let peak = 0;
  for (const ch of pcm.channels) {
    for (let i = 0; i < ch.length; i++) {
      const a = Math.abs(ch[i]!);
      if (a > peak) peak = a;
    }
  }
  if (peak === 0) {
    // All-zero input — nothing to scale. Return a copy to preserve the
    // "pipeline transforms are non-destructive" contract.
    return { sampleRate: pcm.sampleRate, channels: pcm.channels.map((c) => c.slice()) };
  }
  const gain = target / peak;
  const out = pcm.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    for (let i = 0; i < ch.length; i++) dst[i] = ch[i]! * gain;
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels: out };
}
